import React, { useState, useEffect } from "react";
import { Box, CardContent } from "@mui/material";
import { FormCard } from "./styles/StyledComponents";
import { STEPS, INITIAL_FORM_DATA } from "./constants";
import StepHeader from "./components/StepHeader";
import StepNavigation from "./components/StepNavigation";
import SuccessPage from "./components/SuccessPage";
import Step1DatiPersonali from "./components/steps/Step1DatiPersonali";
import Step2Lavoro from "./components/steps/Step2Lavoro";
import Step3Beneficiario from "./components/steps/Step3Beneficiario";
import Step4Questionario from "./components/steps/Step4Questionario";
import Step5Riepilogo from "./components/steps/Step5Riepilogo";
import { useFormRCAutoDetails } from "../../../rcautoContext";

const VitaProtetta = () => {
  const { formState } = useFormRCAutoDetails();
  const [activeStep, setActiveStep] = useState(0);
  const [savedSuccessfully, setSavedSuccessfully] = useState(false);
  const [numeroPreventivo, setNumeroPreventivo] = useState("");
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  // Precompila i campi con i dati del cliente
  useEffect(() => {
    if (formState?.cliente) {
      const cliente = formState.cliente;
      const updates = {};

      if (cliente.nome) updates.nome = cliente.nome;
      if (cliente.cognome) updates.cognome = cliente.cognome;
      if (cliente.email) updates.email = cliente.email;
      if (cliente.telefono) updates.telefono = cliente.telefono;
      
      // Converte data di nascita da DD/MM/YYYY a YYYY-MM-DD
      if (cliente.dataNascita) {
        let dataNascita = cliente.dataNascita;
        if (typeof dataNascita === "string" && dataNascita.includes("/")) {
          const parts = dataNascita.split("/");
          if (parts.length === 3) {
            dataNascita = `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
          }
        }
        updates.dataNascita = dataNascita;
      }

      if (Object.keys(updates).length > 0) {
        setFormData((prev) => ({ ...prev, ...updates }));
      }
    }
  }, [formState?.cliente]);

  const handleNext = () => {
    if (activeStep === STEPS.length - 1) {
      // Genera numero preventivo
      const dataOggi = new Date();
      const numeroCasuale = Math.floor(Math.random() * 1000);
      const numero = `PREV_${dataOggi.getFullYear()}${String(
        dataOggi.getMonth() + 1,
      ).padStart(2, "0")}${String(dataOggi.getDate()).padStart(
        2,
        "0",
      )}_${numeroCasuale}`;
      setNumeroPreventivo(numero);
      setSavedSuccessfully(true);
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleInputChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleReset = () => {
    setFormData(INITIAL_FORM_DATA);
  };

  const isStep1Valid = () => {
    return (
      formData.nome &&
      formData.cognome &&
      formData.dataNascita &&
      formData.sesso &&
      formData.email &&
      formData.telefono &&
      formData.privacyAccepted
    );
  };

  const isStep2Valid = () => {
    return formData.statusLavorativo !== "";
  };

  const isStep3Valid = () => {
    if (!formData.hasBeneficiario) {
      return true;
    }
    return (
      formData.nomeBeneficiario &&
      formData.cognomeBeneficiario &&
      formData.relazioneBeneficiario
    );
  };

  const isStep4Valid = () => {
    return (
      formData.consapevolezzaScoperti &&
      formData.comprensioneRinnovo &&
      (formData.protezioneStandard || formData.protezionePotenziata) &&
      formData.condizioniGenerali &&
      formData.condizioniVendita
    );
  };

  const isNextDisabled = () => {
    switch (activeStep) {
      case 0:
        return !isStep1Valid();
      case 1:
        return !isStep2Valid();
      case 2:
        return !isStep3Valid();
      case 3:
        return !isStep4Valid();
      default:
        return false;
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Step1DatiPersonali
            formData={formData}
            handleInputChange={handleInputChange}
            onReset={handleReset}
          />
        );
      case 1:
        return (
          <Step2Lavoro
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
          />
        );
      case 2:
        return (
          <Step3Beneficiario
            formData={formData}
            handleInputChange={handleInputChange}
            onReset={handleReset}
          />
        );
      case 3:
        return (
          <Step4Questionario
            formData={formData}
            handleInputChange={handleInputChange}
          />
        );
      case 4:
        return <Step5Riepilogo formData={formData} />;
      default:
        return null;
    }
  };

  const handleNewQuote = () => {
    setSavedSuccessfully(false);
    setActiveStep(0);
    setNumeroPreventivo("");
    setFormData(INITIAL_FORM_DATA);
  };

  const handleGoHome = () => {
    window.location.reload();
  };

  if (savedSuccessfully) {
    return (
      <SuccessPage
        numeroPreventivo={numeroPreventivo}
        formData={formData}
        onNewQuote={handleNewQuote}
        onGoHome={handleGoHome}
      />
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <StepHeader activeStep={activeStep} steps={STEPS} />

      <FormCard>
        <CardContent>
          <Box sx={{ mt: 2 }}>{renderStepContent(activeStep)}</Box>

          <StepNavigation
            activeStep={activeStep}
            totalSteps={STEPS.length}
            onBack={handleBack}
            onNext={handleNext}
            isNextDisabled={isNextDisabled()}
          />
        </CardContent>
      </FormCard>
    </Box>
  );
};

export default VitaProtetta;
